import { useEffect, useId, useRef, useState } from 'react';
import type { TagResponse, TagSummary } from '../api';

interface TagInputProps {
  attached: TagSummary[];
  available: TagResponse[];
  onAttach: (name: string) => void;
  onDetach: (tagId: string) => void;
  disabled?: boolean;
  /** What is being tagged, for the screen-reader label. A diagram is not a snippet. */
  subject?: string;
}

/** Matches the API's normalisation, so the UI agrees with the server about what is a duplicate. */
function normalize(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Type-to-filter over existing tags; Enter attaches the highlighted one, or creates and attaches
 * whatever was typed. There is no separate "manage tags" step to reach first.
 */
export function TagInput({
  attached,
  available,
  onAttach,
  onDetach,
  disabled = false,
  subject = 'snippet',
}: TagInputProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const attachedNames = new Set(attached.map((tag) => normalize(tag.name)));
  const needle = normalize(query);

  const suggestions = available
    .filter((tag) => !attachedNames.has(normalize(tag.name)))
    .filter((tag) => (needle ? normalize(tag.name).includes(needle) : true))
    .slice(0, 8);

  // Offer to create only when the typed name is not already an exact suggestion or attached.
  const exactExists =
    attachedNames.has(needle) || available.some((tag) => normalize(tag.name) === needle);
  const canCreate = needle.length > 0 && !exactExists;

  const options = canCreate ? [...suggestions, null] : suggestions;

  useEffect(() => setActiveIndex(0), [query]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function onPointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function attach(name: string) {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }

    onAttach(trimmed);
    setQuery('');
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative flex flex-wrap items-center gap-1.5">
      <ul className="flex flex-wrap gap-1.5" aria-label={`Tags on this ${subject}`}>
        {attached.map((tag) => (
          <li key={tag.id}>
            <span
              className="inline-flex items-center gap-1 rounded-full border border-border-subtle
                bg-surface-raised px-2 py-0.5 text-xs text-text"
            >
              {tag.color && (
                <span
                  aria-hidden="true"
                  className="size-2 rounded-full"
                  style={{ backgroundColor: tag.color }}
                />
              )}
              {tag.name}
              <button
                type="button"
                disabled={disabled}
                onClick={() => onDetach(tag.id)}
                aria-label={`Remove tag ${tag.name}`}
                className="text-text-faint transition-colors hover:text-danger disabled:opacity-50"
              >
                ×
              </button>
            </span>
          </li>
        ))}
      </ul>

      <input
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label="Add a tag"
        placeholder={attached.length === 0 ? 'Add a tag…' : 'Add…'}
        disabled={disabled}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((index) => Math.min(index + 1, options.length - 1));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((index) => Math.max(index - 1, 0));
          } else if (event.key === 'Enter') {
            event.preventDefault();
            const picked = options[activeIndex];
            // null is the "create this" row; anything else is an existing tag.
            attach(picked === null || picked === undefined ? query : picked.name);
          } else if (event.key === 'Escape') {
            setOpen(false);
          } else if (event.key === 'Backspace' && query === '' && attached.length > 0) {
            const last = attached[attached.length - 1];
            if (last) {
              onDetach(last.id);
            }
          }
        }}
        className="min-w-24 flex-1 rounded-md border border-transparent bg-transparent px-1 py-0.5
          text-xs text-text placeholder:text-text-faint hover:border-border-subtle
          focus:border-border-subtle disabled:opacity-50"
      />

      {open && options.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Tag suggestions"
          className="absolute top-full left-0 z-20 mt-1 w-56 overflow-hidden rounded-md border
            border-border-subtle bg-surface-raised py-1 shadow-lg"
        >
          {options.map((tag, index) => (
            <li
              key={tag?.id ?? '__create__'}
              role="option"
              aria-selected={index === activeIndex}
              onPointerDown={(event) => {
                event.preventDefault();
                attach(tag === null ? query : tag.name);
              }}
              onPointerEnter={() => setActiveIndex(index)}
              className={`cursor-pointer px-3 py-1.5 text-xs
                ${index === activeIndex ? 'bg-accent-subtle text-text' : 'text-text-muted'}`}
            >
              {tag === null ? (
                <>
                  Create <span className="font-medium text-text">{query.trim()}</span>
                </>
              ) : (
                <span className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5">
                    {tag.color && (
                      <span
                        aria-hidden="true"
                        className="size-2 shrink-0 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                    )}
                    <span className="truncate">{tag.name}</span>
                  </span>
                  <span className="text-text-faint">{tag.snippetCount}</span>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
